// Montreal Cognitive Assessment (MoCA), Version 7.1 — Original.
//
// © Z. Nasreddine, MD — https://mocatest.org
//
// LICENSING — IMPORTANT (do NOT remove this comment before production):
// The MoCA is a copyrighted clinical instrument. Since ~2020 the MoCA Clinic
// & Institute requires:
//   1. Provider certification (the MoCA Training & Certification Program), and
//   2. A paid annual license for any digital / electronic implementation.
// Before this surface ships outside of demo/pilot use, MediLink (or the
// administering practice) must execute a licensing agreement with the MoCA
// Clinic. Until then this is a clinically-shaped demo built off a public
// courtesy copy of the v7.1 form for visual review purposes.
//
// Scoring summary (matches the printed form):
//   * Visuospatial / Executive    /5
//   * Naming                      /3
//   * Memory (registration)       /0   (no score — words are recalled later)
//   * Attention                   /6   (digit-fwd /1, digit-bwd /1, vigilance /1, serial-7 /3)
//   * Language                    /3   (two sentence repetitions /2, F-fluency /1)
//   * Abstraction                 /2
//   * Delayed Recall              /5
//   * Orientation                 /6
//   TOTAL                         /30
//   +1 bonus point if patient has ≤ 12 years of formal education.
//   Normal ≥ 26.

export const MEMORY_WORDS = ['FACE', 'VELVET', 'CHURCH', 'DAISY', 'RED'];

// Synonym lists used by interactive mode when the patient types an answer.
// Match is case-insensitive and ignores leading/trailing whitespace.
// Keep these conservative — too permissive defeats the screening value.
export const NAMING_ANSWERS = {
  lion:  ['lion', 'lions'],
  rhino: ['rhinoceros', 'rhino', 'rhinos', 'rhinocerous'], // common misspelling
  camel: ['camel', 'camels', 'dromedary'],
};

// Sequences shown / read for the digit-span items.
export const DIGIT_FORWARD  = [2, 1, 8, 5, 4];
export const DIGIT_BACKWARD = [7, 4, 2];

// Vigilance letter stream (printed on the form). Patient taps on each A.
export const VIGILANCE_LETTERS = 'FBACMNAAJKLBAFAKDEAAAJAMOFAAB'.split('');

// Trail-making node positions on a 0-100 unit grid (so the SVG can scale).
// Order in this array is irrelevant; the patient must tap them in the order
// given by TRAIL_SEQUENCE.
export const TRAIL_NODES = [
  { id: '1',     label: '1',     x: 18, y: 55, sublabel: 'Begin' },
  { id: 'A',     label: 'A',     x: 50, y: 16 },
  { id: '2',     label: '2',     x: 78, y: 30 },
  { id: 'B',     label: 'B',     x: 50, y: 40 },
  { id: '3',     label: '3',     x: 70, y: 64 },
  { id: 'C',     label: 'C',     x: 20, y: 90 },
  { id: '4',     label: '4',     x: 46, y: 70 },
  { id: 'D',     label: 'D',     x: 14, y: 80 },
  { id: '5',     label: '5',     x:  8, y: 38 },
  { id: 'E',     label: 'E',     x: 26, y: 22, sublabel: 'End' },
];
export const TRAIL_SEQUENCE = ['1', 'A', '2', 'B', '3', 'C', '4', 'D', '5', 'E'];

// Normalize a free-text answer for matching: trim, lowercase, strip
// non-alphanumerics. Used by naming + delayed-recall.
export function normalizeAnswer(s) {
  return String(s || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Does the typed answer match any accepted variant?
export function matchesAnswer(typed, accepted) {
  const n = normalizeAnswer(typed);
  if (!n) return false;
  return accepted.some(a => normalizeAnswer(a) === n);
}

// For delayed recall: split a free-text response into recalled words and
// match each against the MEMORY_WORDS list. Returns the set of matched words.
export function matchRecalledWords(text, words = MEMORY_WORDS) {
  const tokens = String(text || '')
    .split(/[\s,;]+/)
    .map(normalizeAnswer)
    .filter(Boolean);
  const matched = new Set();
  for (const w of words) {
    const target = normalizeAnswer(w);
    if (tokens.includes(target)) matched.add(w);
  }
  return matched;
}

// For F-fluency: count valid F-words in a typed list. Strips proper nouns
// only loosely (capitalised single words still count — provider can verify).
// Counts unique words starting with F/f.
export function countFluency(text) {
  const tokens = String(text || '')
    .split(/[\s,;]+/)
    .map(t => t.trim().toLowerCase())
    .filter(t => t.length > 1 && /^f/.test(t));
  return new Set(tokens).size;
}

// Each "step" in the conversational flow. Sections are visually grouped but
// the user advances one step at a time.
//
// step.scoring:
//   { kind: 'binary',        max: 1 }                  → 0 or 1 button
//   { kind: 'binary-multi',  max: N, items: [...] }    → N binary items grouped
//   { kind: 'fluency',       max: 1, threshold: 11 }   → count input, awarded if ≥ threshold
//   { kind: 'serial7',       max: 3 }                  → check 5 boxes; map to 0/1/2/3
//   { kind: 'recall',        max: 5, words: [...] }    → 5 word toggles
//   { kind: 'memory-trial',  max: 0, words: [...] }    → observation only, no score
//
// All steps return { score, observations } when complete.

export const MOCA = {
  id: 'moca-v7.1',
  name: 'Montreal Cognitive Assessment (MoCA)',
  version: '7.1 Original',
  attribution: '© Z. Nasreddine, MD · mocatest.org',
  duration_minutes: 10,
  max_score: 30,
  normal_threshold: 26,
  education_bonus_max_years: 12,

  sections: [
    {
      id: 'intro',
      title: 'Before you begin',
      max: 0,
      steps: [
        {
          id: 'intro-setup',
          kind: 'intro',
          headline: "Let's run the MoCA together.",
          body: "This is a guided cognitive screening. You'll walk through eight short sections at the patient's pace. Each section takes a few seconds to a couple of minutes. The platform scores as you go.",
          notes: [
            'Quiet, well-lit room. Patient seated comfortably.',
            'Have a pen and paper ready for the trail-making, cube, and clock tasks.',
            'You are the administrator — read the prompts aloud, observe the response, score on this screen.',
          ],
        },
      ],
    },

    // -----------------------------------------------------------------
    // 1. Visuospatial / Executive ............................ /5
    // -----------------------------------------------------------------
    {
      id: 'visuospatial',
      title: 'Visuospatial / Executive',
      max: 5,
      steps: [
        {
          id: 'trail-making',
          section: 'visuospatial',
          kind: 'binary',
          max: 1,
          headline: 'Trail Making',
          instruction: "Ask the patient to draw a line, going from a number to a letter in ascending order, alternating between numbers and letters.",
          example: '1 → A → 2 → B → 3 → C → 4 → D → 5 → E',
          scoring_rule: 'Score 1 point if the sequence is drawn correctly with no errors. Any error that is not immediately self-corrected = 0.',
        },
        {
          id: 'cube-copy',
          section: 'visuospatial',
          kind: 'binary',
          max: 1,
          headline: 'Copy Cube',
          instruction: 'Show the patient the 3D cube on the form and ask them to copy it as accurately as they can in the space provided.',
          scoring_rule: 'Score 1 point if the drawing is 3-dimensional, all lines are drawn, no line is added, and lines are relatively parallel. Length can vary.',
        },
        {
          id: 'clock-contour',
          section: 'visuospatial',
          kind: 'binary',
          max: 1,
          headline: 'Clock — Contour',
          instruction: 'Ask the patient to draw a clock showing "ten past eleven". This step scores the contour only.',
          scoring_rule: 'Score 1 point if the clock face is a circle, with only a minor distortion acceptable (e.g., slight imperfection on closing).',
        },
        {
          id: 'clock-numbers',
          section: 'visuospatial',
          kind: 'binary',
          max: 1,
          headline: 'Clock — Numbers',
          instruction: 'Same drawing as the previous step. Score the numbers.',
          scoring_rule: 'Score 1 point if all numbers are present, in correct order, and roughly in the right quadrants. Roman numerals are acceptable. No extra numbers allowed.',
        },
        {
          id: 'clock-hands',
          section: 'visuospatial',
          kind: 'binary',
          max: 1,
          headline: 'Clock — Hands',
          instruction: 'Same drawing. Score the hands now.',
          scoring_rule: 'Score 1 point if both hands are present, the hour hand is clearly shorter than the minute hand, and the hands are anchored at the centre of the clock face.',
        },
      ],
    },

    // -----------------------------------------------------------------
    // 2. Naming .............................................. /3
    // -----------------------------------------------------------------
    {
      id: 'naming',
      title: 'Naming',
      max: 3,
      steps: [
        {
          id: 'naming-all',
          section: 'naming',
          kind: 'binary-multi',
          max: 3,
          headline: 'Naming',
          instruction: 'Beginning on the left, point to each figure and say "Tell me the name of this animal."',
          items: [
            { id: 'lion', label: 'Lion' },
            { id: 'rhino', label: 'Rhinoceros' },
            { id: 'camel', label: 'Camel (or dromedary)' },
          ],
          scoring_rule: 'Score 1 point per animal named correctly.',
        },
      ],
    },

    // -----------------------------------------------------------------
    // 3. Memory — registration (no points; recalled in step 7) ..  /0
    // -----------------------------------------------------------------
    {
      id: 'memory-registration',
      title: 'Memory (registration)',
      max: 0,
      steps: [
        {
          id: 'memory-read',
          section: 'memory-registration',
          kind: 'memory-trial',
          max: 0,
          words: MEMORY_WORDS,
          headline: 'Memory — read the list',
          instruction: 'Read this list of words at a rate of one per second. Then say: "I am going to read a list of words that you will have to remember now and later. Listen carefully. When I am through, tell me as many words as you can remember. It doesn\'t matter what order you say them in." After the first attempt, read the list a second time and say: "I am going to read the same list for a second time. Try to remember and tell me as many words as you can, including the words you said the first time."',
          subtext: 'No points are scored here. Mark which words the patient recalled in each trial — these are not graded, but help interpret results later. The five words will be tested again as Delayed Recall after about five minutes.',
        },
      ],
    },

    // -----------------------------------------------------------------
    // 4. Attention ........................................... /6
    // -----------------------------------------------------------------
    {
      id: 'attention',
      title: 'Attention',
      max: 6,
      steps: [
        {
          id: 'digit-forward',
          section: 'attention',
          kind: 'binary',
          max: 1,
          headline: 'Digit span — forward',
          instruction: 'Read the digit sequence at a rate of one digit per second. Ask the patient to repeat them in the same order.',
          example: '2 — 1 — 8 — 5 — 4',
          scoring_rule: 'Score 1 point for an exact repetition.',
        },
        {
          id: 'digit-backward',
          section: 'attention',
          kind: 'binary',
          max: 1,
          headline: 'Digit span — backward',
          instruction: 'Read the digit sequence at a rate of one digit per second. Ask the patient to repeat them in the reverse order.',
          example: '7 — 4 — 2',
          scoring_rule: 'Score 1 point for the correct reverse sequence (2-4-7).',
        },
        {
          id: 'vigilance',
          section: 'attention',
          kind: 'vigilance',
          max: 1,
          headline: 'Vigilance — tap on A',
          instruction: 'Read the list of letters at a rate of one per second. Ask the patient to tap with their hand each time the letter A is read. No tapping for any other letter.',
          letters: 'F B A C M N A A J K L B A F A K D E A A A J A M O F A A B',
          scoring_rule: 'Score 1 point if there are zero or one error. Two or more errors (incorrect tap on a non-A, or missing a real A) = 0 points.',
        },
        {
          id: 'serial-7',
          section: 'attention',
          kind: 'serial7',
          max: 3,
          headline: 'Serial 7 subtraction',
          instruction: 'Ask the patient: "Now I will ask you to count by subtracting 7 from 100, and then keep subtracting 7 from your answer until I tell you to stop."',
          targets: [93, 86, 79, 72, 65],
          scoring_rule: '4 or 5 correct = 3 points · 2 or 3 correct = 2 · 1 correct = 1 · 0 correct = 0. Each subtraction is judged independently — give credit if the patient subtracts 7 correctly from a wrong number.',
        },
      ],
    },

    // -----------------------------------------------------------------
    // 5. Language ............................................ /3
    // -----------------------------------------------------------------
    {
      id: 'language',
      title: 'Language',
      max: 3,
      steps: [
        {
          id: 'sentence-1',
          section: 'language',
          kind: 'binary',
          max: 1,
          headline: 'Sentence repetition — 1 of 2',
          instruction: 'Say to the patient: "I am going to read you a sentence. Repeat it after me, exactly as I say it." Then read the sentence below.',
          example: '"I only know that John is the one to help today."',
          scoring_rule: 'Score 1 point only if the sentence is repeated exactly. Errors include omission (e.g., "only", "always"), substitution / addition ("John is the one to help today" or "hides" for "hid"), or alteration of word order.',
        },
        {
          id: 'sentence-2',
          section: 'language',
          kind: 'binary',
          max: 1,
          headline: 'Sentence repetition — 2 of 2',
          instruction: 'Say: "Now I am going to read you another sentence. Repeat it after me, exactly as I say it."',
          example: '"The cat always hid under the couch when dogs were in the room."',
          scoring_rule: 'Same scoring rule as the previous sentence — exact repetition required.',
        },
        {
          id: 'fluency',
          section: 'language',
          kind: 'fluency',
          max: 1,
          threshold: 11,
          headline: 'Verbal fluency',
          instruction: 'Say: "Tell me as many words as you can think of that begin with a certain letter of the alphabet that I will tell you in a moment. You can say any kind of word you want, except proper nouns (like Bob or Boston), numbers, or words that begin with the same sound but have a different suffix, for example: love, lover, loving. I will tell you to stop after one minute. Are you ready? The letter is F."',
          subtext: 'Time the patient for exactly one minute. Count the total number of unique acceptable words.',
          scoring_rule: 'Score 1 point if the patient produces 11 or more words in 60 seconds.',
        },
      ],
    },

    // -----------------------------------------------------------------
    // 6. Abstraction ......................................... /2
    // -----------------------------------------------------------------
    {
      id: 'abstraction',
      title: 'Abstraction',
      max: 2,
      steps: [
        {
          id: 'abstraction-pairs',
          section: 'abstraction',
          kind: 'binary-multi',
          max: 2,
          headline: 'Abstraction',
          instruction: 'Say to the patient: "Tell me how an orange and a banana are alike." If the patient answers concretely, then say only one additional time: "Tell me another way in which those items are alike." If the patient does not give the appropriate response (fruit), say "Yes, and they are also both fruit." Do not give any additional instructions or clarification. After the practice trial, score each of the following pairs.',
          items: [
            { id: 'train-bicycle', label: 'Train — Bicycle', hint: 'Acceptable: means of transportation, travelling. Not acceptable: they have wheels.' },
            { id: 'watch-ruler',   label: 'Watch — Ruler',   hint: 'Acceptable: measuring instruments, used to measure. Not acceptable: they have numbers.' },
          ],
          scoring_rule: 'Score 1 point per pair only when the patient gives a categorical or abstract answer.',
        },
      ],
    },

    // -----------------------------------------------------------------
    // 7. Delayed Recall ...................................... /5
    // -----------------------------------------------------------------
    {
      id: 'delayed-recall',
      title: 'Delayed Recall',
      max: 5,
      steps: [
        {
          id: 'recall-uncued',
          section: 'delayed-recall',
          kind: 'recall',
          max: 5,
          words: MEMORY_WORDS,
          headline: 'Delayed Recall — uncued',
          instruction: 'Say: "I read some words to you earlier, which I asked you to remember. Tell me as many of those words as you can remember." Mark each word the patient recalls without any cue.',
          subtext: 'Only WITH NO CUE recall counts for the score. Category cue and multiple-choice cue are tracked for clinical interpretation but do not add points.',
          scoring_rule: 'Score 1 point per word recalled spontaneously, with no cue.',
        },
      ],
    },

    // -----------------------------------------------------------------
    // 8. Orientation ......................................... /6
    // -----------------------------------------------------------------
    {
      id: 'orientation',
      title: 'Orientation',
      max: 6,
      steps: [
        {
          id: 'orientation-all',
          section: 'orientation',
          kind: 'binary-multi',
          max: 6,
          headline: 'Orientation',
          instruction: 'Say: "Tell me the date today." If the patient does not give a complete answer, prompt with: "Tell me the year, month, exact date, and day of the week." Then say: "Now, tell me the name of this place, and which city it is in."',
          items: [
            { id: 'date',  label: 'Date'  },
            { id: 'month', label: 'Month' },
            { id: 'year',  label: 'Year'  },
            { id: 'day',   label: 'Day of week' },
            { id: 'place', label: 'Place / clinic name' },
            { id: 'city',  label: 'City' },
          ],
          scoring_rule: 'Score 1 point per correct item. Date must be exact. No points for approximations.',
        },
      ],
    },
  ],
};

// Flatten sections → ordered list of steps, with section context attached.
// This is what the conversational driver iterates over.
export function flattenSteps(assessment = MOCA) {
  const out = [];
  for (const section of assessment.sections) {
    for (const step of section.steps) {
      out.push({
        ...step,
        section_id: section.id,
        section_title: section.title,
        section_max: section.max,
      });
    }
  }
  return out;
}

// Total a scored response set, applying the education bonus if eligible.
// `answers` is keyed by step.id; value is { score, ... }.
export function scoreAssessment(answers, { educationYears = null } = {}) {
  const bySection = {};
  let total = 0;
  for (const section of MOCA.sections) {
    let s = 0;
    for (const step of section.steps) {
      const a = answers[step.id];
      if (a && typeof a.score === 'number') s += a.score;
    }
    bySection[section.id] = { title: section.title, score: s, max: section.max };
    total += s;
  }
  const bonusApplied =
    educationYears != null && educationYears <= MOCA.education_bonus_max_years && total < MOCA.max_score;
  if (bonusApplied) total += 1;
  return {
    total,
    max: MOCA.max_score,
    bySection,
    bonusApplied,
    normalThreshold: MOCA.normal_threshold,
    isNormal: total >= MOCA.normal_threshold,
  };
}
